import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../button';

dayjs.extend(utc);
dayjs.extend(timezone);

import { MonthYearPicker } from './month-year-picker';
import {
  CalendarContainer,
  CalendarGrid,
  DayCell,
  DayLabel,
  MonthButton,
  MonthNavigation,
  QuickRangeButton,
  QuickRanges,
} from './styles';
import { TimeInput } from './time-input';
import { TimePicker } from './time-picker';

import { ICONS } from '@controlrooms/constants';

export interface DateRangePickerProps {
  value?: [Date, Date] | null;
  onChange?: (value: [Date, Date] | null) => void;
  minDate?: Date;
  maxDate?: Date;
  showOneCalendar?: boolean; //To be implemented
  ranges?: Array<{
    label: string;
    value: [Date, Date];
  }>;
  selectionMode?: 'start' | 'end' | 'both';
  onMonthYearChange?: (date: Date) => void;
  onTimeChange?: (time: Date) => void;
  currentTimeType?: 'start' | 'end'; // Which time we're editing
  timeFormat?: '12h' | '24h';
  initialMonth?: Date; // Initial month to display
  autoUpdateMonth?: boolean; // Whether to auto-update month when value changes
  disableRangeHighlight?: boolean; // Disable range highlighting (for single date mode)
  includeTime?: boolean; // Whether to show time picker
  includeSeconds?: boolean; // Whether to show seconds in time picker
  externalHoveredDate?: Date | null; // External hover state for dual calendar mode
  onMouseEnter?: (date: Date) => void; // External mouse enter handler
  onMouseLeave?: () => void; // External mouse leave handler
  timezone?: string; // Timezone for time extraction
}

export const CRDateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  ranges = [],
  selectionMode = 'both',
  onMonthYearChange,
  onTimeChange,
  currentTimeType,
  timeFormat = '24h',
  initialMonth,
  autoUpdateMonth = true,
  disableRangeHighlight = false,
  includeTime = true,
  includeSeconds = false,
  externalHoveredDate,
  onMouseEnter,
  onMouseLeave,
  timezone,
}) => {
  const [currentDate, setCurrentDate] = useState(() => {
    if (initialMonth) {
      return initialMonth;
    }
    // If no initialMonth, create date in the selected timezone
    if (timezone) {
      return dayjs().tz(timezone).toDate();
    }
    return new Date();
  });
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [isMonthYearPickerOpen, setIsMonthYearPickerOpen] = useState(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime, setEndTime] = useState('23:59:59');

  // Update current date when value changes (only if autoUpdateMonth is true)
  useEffect(() => {
    if (value && value[0] && autoUpdateMonth) {
      // Convert UTC to local timezone for calendar display
      const localDate = timezone ? dayjs(value[0]).tz(timezone).toDate() : value[0];

      setCurrentDate(localDate);
    }
    if (value && value[0]) {
      // Extract time from start date in the specified timezone
      const startTimeInTz = timezone ? dayjs(value[0]).tz(timezone) : dayjs(value[0]);
      const startHours = startTimeInTz.hour().toString().padStart(2, '0');
      const startMinutes = startTimeInTz.minute().toString().padStart(2, '0');
      const startSeconds = startTimeInTz.second().toString().padStart(2, '0');
      setStartTime(`${startHours}:${startMinutes}:${startSeconds}`);
    }
    if (value && value[1]) {
      // Extract time from end date in the specified timezone
      const endTimeInTz = timezone ? dayjs(value[1]).tz(timezone) : dayjs(value[1]);
      const endHours = endTimeInTz.hour().toString().padStart(2, '0');
      const endMinutes = endTimeInTz.minute().toString().padStart(2, '0');
      const endSeconds = endTimeInTz.second().toString().padStart(2, '0');
      setEndTime(`${endHours}:${endMinutes}:${endSeconds}`);
    }
  }, [value, autoUpdateMonth, timezone]);

  // Update current date when selectionMode changes to show the correct month for the active tab
  useEffect(() => {
    if (autoUpdateMonth) {
      if (value && selectionMode === 'start' && value[0]) {
        setCurrentDate(value[0]);
      } else if (value && selectionMode === 'end' && value[1]) {
        setCurrentDate(value[1]);
      }
    }
  }, [selectionMode, value, autoUpdateMonth]);

  // Update current date when initialMonth changes
  useEffect(() => {
    if (initialMonth) {
      setCurrentDate(initialMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMonth?.getFullYear(), initialMonth?.getMonth()]);

  const getDaysInMonth = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    while (days.length < 42) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, []);

  const days = useMemo(() => getDaysInMonth(currentDate), [currentDate, getDaysInMonth]);
  const dayLabels = ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'];

  const isInRange = useCallback(
    (date: Date) => {
      if (!value || disableRangeHighlight) return false;

      // Check if the date is in the visible month range of this calendar
      const isDateVisible = (() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Get the first day of the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        // Get the last day of the current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

        const dateTime = date.getTime();
        // Check if the date falls within the visible month range
        return dateTime >= firstDayOfMonth.getTime() && dateTime <= lastDayOfMonth.getTime();
      })();

      // Only show range highlights for dates that are visible in this calendar
      if (!isDateVisible) {
        return false;
      }

      // Convert UTC values to the selected timezone for proper comparison
      const startInTz = dayjs.utc(value[0]).tz(timezone);
      const endInTz = dayjs.utc(value[1]).tz(timezone);

      // Convert the calendar date to the same timezone for comparison,
      // interpreting its components directly in the target timezone at midnight.
      const dateInTz = dayjs.tz(
        `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
          .getDate()
          .toString()
          .padStart(2, '0')} 00:00:00`,
        timezone,
      );

      // Compare dates in the same timezone with exclusive range (exclude start and end dates)
      const isInRangeResult =
        dateInTz.isAfter(startInTz, 'day') && dateInTz.isBefore(endInTz, 'day');

      return isInRangeResult;
    },
    [value, disableRangeHighlight, currentDate, timezone],
  );

  const isStartDate = useCallback(
    (date: Date) => {
      if (!value) return false;

      // Check if the date is in the visible month range of this calendar
      const isDateVisible = (() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Get the first day of the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        // Get the last day of the current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

        const dateTime = date.getTime();
        // Check if the date falls within the visible month range
        return dateTime >= firstDayOfMonth.getTime() && dateTime <= lastDayOfMonth.getTime();
      })();

      // Only show start date highlight if the date is visible in this calendar
      if (!isDateVisible) {
        return false;
      }

      // Convert UTC date to calendar timezone for comparison
      const startDateInCalendarTz = timezone
        ? new Date(dayjs.utc(value[0]).tz(timezone).format('YYYY-MM-DD HH:mm:ss'))
        : value[0];

      return date.toDateString() === startDateInCalendarTz.toDateString();
    },
    [value, currentDate, timezone],
  );

  const isEndDate = useCallback(
    (date: Date) => {
      if (!value) return false;

      // Check if the date is in the visible month range of this calendar
      const isDateVisible = (() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Get the first day of the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        // Get the last day of the current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

        const dateTime = date.getTime();
        // Check if the date falls within the visible month range
        return dateTime >= firstDayOfMonth.getTime() && dateTime <= lastDayOfMonth.getTime();
      })();

      // Only show end date highlight if the date is visible in this calendar
      if (!isDateVisible) {
        return false;
      }

      // Convert UTC date to calendar timezone for comparison
      const endDateInCalendarTz = timezone
        ? new Date(dayjs.utc(value[1]).tz(timezone).format('YYYY-MM-DD HH:mm:ss'))
        : value[1];

      return date.toDateString() === endDateInCalendarTz.toDateString();
    },
    [value, currentDate, timezone],
  );

  const isOtherMonth = useCallback(
    (date: Date) => {
      return date.getMonth() !== currentDate.getMonth();
    },
    [currentDate],
  );

  const isDisabled = useCallback(
    (date: Date) => {
      if (minDate && date < minDate) return true;
      if (maxDate && date > maxDate) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const isInHoverRange = useCallback(
    (date: Date) => {
      // Use external hover state if available, otherwise use internal hover state
      const activeHoveredDate =
        externalHoveredDate !== undefined ? externalHoveredDate : hoveredDate;

      // Show hover preview when we have a start date and are hovering over a potential end date
      // Only show hover highlights when selecting end date (not start date)
      if (
        !value ||
        !value[0] ||
        !activeHoveredDate ||
        disableRangeHighlight ||
        selectionMode === 'start'
      )
        return false;

      const start = value[0];
      const hoverTime = activeHoveredDate.getTime();
      const dateTime = date.getTime();

      // Check if the date is in the visible month range of this calendar
      const isDateVisible = (() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Get the first day of the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        // Get the last day of the current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

        // Check if the date falls within the visible month range
        return dateTime >= firstDayOfMonth.getTime() && dateTime <= lastDayOfMonth.getTime();
      })();

      // Only show hover highlights for dates that are visible in this calendar
      if (!isDateVisible) {
        return false;
      }

      // If hovering over a date on or after start date, show range from start to hover
      return dateTime >= start.getTime() && dateTime <= hoverTime;
    },
    [value, hoveredDate, externalHoveredDate, disableRangeHighlight, selectionMode, currentDate],
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      if (isDisabled(date)) return;

      // Convert local date selection to UTC
      const convertToUTC = (localDate: Date): Date => {
        if (timezone) {
          // Create dayjs object in the specified timezone, then convert to UTC
          const year = localDate.getFullYear();
          const month = localDate.getMonth();
          const day = localDate.getDate();
          const hour = localDate.getHours();
          const minute = localDate.getMinutes();
          const second = localDate.getSeconds();

          return dayjs
            .tz(
              `${year}-${(month + 1).toString().padStart(2, '0')}-${day
                .toString()
                .padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute
                .toString()
                .padStart(2, '0')}:${second.toString().padStart(2, '0')}`,
              timezone,
            )
            .utc()
            .toDate();
        }
        return localDate;
      };

      if (selectionMode === 'start') {
        // Only selecting start date - keep existing end date
        if (value && value[1]) {
          // Extract current start time
          const currentStartInTz = dayjs.utc(value[0]).tz(timezone);

          // Create new start date with clicked date + current start time
          // Extract date components from clicked date (these are correct)
          const clickedYear = date.getFullYear();
          const clickedMonth = date.getMonth();
          const clickedDay = date.getDate();

          // Create date in calendar timezone with correct date components
          const selectedDateInTz = dayjs.tz(
            `${clickedYear}-${(clickedMonth + 1).toString().padStart(2, '0')}-${clickedDay
              .toString()
              .padStart(2, '0')}`,
            timezone,
          );
          const convertedSelectedDateTime = selectedDateInTz
            .hour(currentStartInTz.hour())
            .minute(currentStartInTz.minute())
            .second(currentStartInTz.second());

          const newStartDate = convertedSelectedDateTime.utc().toDate();

          const newRange: [Date, Date] = [newStartDate, value[1]];
          onChange?.(newRange);
        } else {
          const newRange: [Date, Date] = [convertToUTC(date), convertToUTC(date)];
          onChange?.(newRange);
        }
      } else if (selectionMode === 'end') {
        // Only selecting end date - keep existing start date
        if (value && value[0]) {
          // Allow same date - validation will be handled by time comparison
          const newRange: [Date, Date] = [value[0], convertToUTC(date)];
          onChange?.(newRange);
        } else {
          const newRange: [Date, Date] = [convertToUTC(date), convertToUTC(date)];
          onChange?.(newRange);
        }
      } else {
        // Default behavior - select both dates
        if (!isSelectingEnd) {
          // First click - set start date
          const newRange: [Date, Date] = [convertToUTC(date), convertToUTC(date)];
          onChange?.(newRange);
          setIsSelectingEnd(true);
        } else {
          // Second click - set end date
          if (value) {
            // Allow same date - validation will be handled by time comparison
            const newRange: [Date, Date] = [value[0], convertToUTC(date)];
            onChange?.(newRange);
            setIsSelectingEnd(false);
          }
        }
      }
    },
    [isSelectingEnd, value, onChange, isDisabled, selectionMode, timezone],
  );

  const handleMouseEnter = useCallback(
    (date: Date) => {
      // Use external handler if available, otherwise use internal logic
      if (onMouseEnter) {
        onMouseEnter(date);
      } else {
        // Show hover preview when we have a start date and are hovering over a potential end date
        if (
          value &&
          value[0] &&
          !isDisabled(date) &&
          date >= value[0] // Show hover for dates on or after the start date
        ) {
          setHoveredDate(date);
        }
      }
    },
    [value, isDisabled, onMouseEnter],
  );

  const handleMouseLeave = useCallback(() => {
    if (onMouseLeave) {
      onMouseLeave();
    } else {
      setHoveredDate(null);
    }
  }, [onMouseLeave]);

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  }, []);

  const handleQuickRange = useCallback(
    (range: [Date, Date]) => {
      onChange?.(range);
      setIsSelectingEnd(false);
    },
    [onChange],
  );

  // Handle time changes
  const handleStartTimeChange = useCallback(
    (time: string) => {
      setStartTime(time);
      if (onTimeChange && value?.[0]) {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        // Create new time in the specified timezone context
        const newTime = timezone
          ? dayjs(value[0]).tz(timezone).hour(hours).minute(minutes).second(seconds).toDate()
          : new Date(value[0].setHours(hours, minutes, seconds));
        onTimeChange(newTime);
      }
    },
    [onTimeChange, value, timezone],
  );

  const handleEndTimeChange = useCallback(
    (time: string) => {
      setEndTime(time);
      if (onTimeChange && value?.[1]) {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        // Create new time in the specified timezone context
        const newTime = timezone
          ? dayjs(value[1]).tz(timezone).hour(hours).minute(minutes).second(seconds).toDate()
          : new Date(value[1].setHours(hours, minutes, seconds));
        onTimeChange(newTime);
      }
    },
    [onTimeChange, value, timezone],
  );

  return (
    <CalendarContainer>
      {ranges.length > 0 && (
        <QuickRanges>
          {ranges.map((range, index) => (
            <QuickRangeButton key={index} onClick={() => handleQuickRange(range.value)}>
              {range.label}
            </QuickRangeButton>
          ))}
        </QuickRanges>
      )}

      <MonthNavigation style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {/* <MonthButton onClick={() => navigateMonth('prev')}>←</MonthButton> */}
          <MonthButton
            style={{
              transform: 'rotate(180deg)',
            }}
            buttonType="text"
            buttonSize="large"
            iconName={ICONS.ArrowRight}
            onClick={() => navigateMonth('prev')}
          />
          <MonthButton
            buttonType="text"
            buttonSize="large"
            onClick={() => {
              setIsMonthYearPickerOpen(!isMonthYearPickerOpen);
              setIsTimePickerOpen(false);
            }}
            style={{
              background: 'transparent',
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            {currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </MonthButton>
          <MonthButton
            buttonType="text"
            buttonSize="large"
            iconName={ICONS.ArrowRight}
            onClick={() => navigateMonth('next')}
          />
          {/* <MonthButton onClick={() => navigateMonth('next')}>→</MonthButton> */}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {includeTime && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '4px',
                  width: '100px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                  {currentTimeType === 'start' ? (
                    <TimeInput
                      value={startTime}
                      onChange={handleStartTimeChange}
                      timeFormat={timeFormat}
                    />
                  ) : (
                    <TimeInput
                      value={endTime}
                      onChange={handleEndTimeChange}
                      timeFormat={timeFormat}
                    />
                  )}
                </div>
              </div>
              <Button
                buttonType="text"
                buttonSize="small"
                iconName="clock"
                onClick={() => {
                  setIsTimePickerOpen(!isTimePickerOpen);
                  setIsMonthYearPickerOpen(false);
                }}
              />
            </>
          )}
        </div>
      </MonthNavigation>

      {isMonthYearPickerOpen && (
        <MonthYearPicker
          currentDate={currentDate}
          onSelect={(date) => {
            setCurrentDate(date);
            setIsMonthYearPickerOpen(false);
            onMonthYearChange?.(date);
          }}
          onClose={() => setIsMonthYearPickerOpen(false)}
        />
      )}

      {isTimePickerOpen && includeTime && (
        <div>
          <div style={{ display: 'flex', gap: '16px' }}>
            {currentTimeType === 'start' ? (
              <div style={{ flex: 1 }}>
                <TimePicker
                  value={startTime}
                  onChange={handleStartTimeChange}
                  timeFormat={timeFormat}
                  includeSeconds={includeSeconds}
                />
              </div>
            ) : (
              <div style={{ flex: 1 }}>
                <TimePicker
                  value={endTime}
                  onChange={handleEndTimeChange}
                  timeFormat={timeFormat}
                  includeSeconds={includeSeconds}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!isMonthYearPickerOpen && !isTimePickerOpen && (
        <CalendarGrid>
          {dayLabels.map((label) => (
            <DayLabel key={label}>{label}</DayLabel>
          ))}

          {days.map((date, index) => (
            <DayCell
              key={index}
              isOtherMonth={isOtherMonth(date)}
              isStartDate={isStartDate(date)}
              isEndDate={isEndDate(date)}
              isInRange={isInRange(date)}
              isInHoverRange={isInHoverRange(date)}
              isDisabled={isDisabled(date)}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => handleMouseEnter(date)}
              onMouseLeave={handleMouseLeave}
            >
              {date.getDate()}
            </DayCell>
          ))}
        </CalendarGrid>
      )}
    </CalendarContainer>
  );
};
